import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useEffect, useMemo, useState } from 'react';

function cleanSubject(subject = '') {
  return String(subject || '(no subject)').replace(/^(re|fw|fwd):\s*/i, '').trim() || '(no subject)';
}

function compactEmail(email = '') {
  const clean = String(email || '').trim();
  if (!clean) return '';
  if (clean.length <= 36) return clean;

  const [local, domain = ''] = clean.split('@');
  if (!domain) return `${clean.slice(0, 30)}…`;

  return `${local.slice(0, 17)}…@${domain.replace(/^mail\./, '')}`;
}

function formatDate(value, { includeTime = false } = {}) {
  if (!value) return '';

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';

  try {
    return new Intl.DateTimeFormat(undefined, includeTime
      ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric' }).format(date);
  } catch {
    return '';
  }
}

function categoryLabel(item = {}) {
  const clean = String(item.categoryTitle || item.category || item.categoryId || '')
    .replace(/_/g, ' ')
    .trim();

  if (!clean || /^(follow[ -]?up|waiting for reply)$/i.test(clean)) return '';

  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function statusLabel(status = '') {
  const map = {
    watching: 'Open',
    drafted: 'Draft ready',
    draft_deleted: 'Draft deleted',
    waiting_after_followup: 'Waiting for reply',
    replied: 'Replied',
    failed: 'Needs attention',
    needs_review: 'Review needed',
    dismissed: 'Dismissed',
    cancelled: 'Dismissed',
    stopped: 'Stopped',
    completed: 'Complete',
    mailbox_disconnected: 'Open',
  };

  return map[status] || status || 'Open';
}

function statusTone(status = '') {
  if (status === 'drafted') return { bg: 'rgba(255,179,71,.12)', color: '#8a5400' };
  if (status === 'waiting_after_followup' || status === 'replied' || status === 'completed') return { bg: 'rgba(70,201,142,.12)', color: '#176b49' };
  if (status === 'failed' || status === 'draft_deleted') return { bg: 'rgba(255,142,122,.11)', color: '#9f3b2c' };
  if (status === 'needs_review') return { bg: 'rgba(255,179,71,.12)', color: '#8a5400' };
  return { bg: 'rgba(26,29,49,.06)', color: '#575b74' };
}

function StatusChip({ status }) {
  const tone = statusTone(status);

  return (
    <Chip
      size="small"
      label={statusLabel(status)}
      sx={{
        height: 24,
        borderRadius: 1,
        bgcolor: tone.bg,
        color: tone.color,
        fontWeight: 800,
        '& .MuiChip-label': { px: 1, fontSize: 11.5 },
      }}
    />
  );
}

function itemKey(item = {}) {
  return String(item.threadId || item.id || item.providerThreadId || item.subject || '');
}

function dateValue(item = {}) {
  return item.lastOutboundAt || item.lastMessageAt || item.updatedAt || item.createdAt || '';
}

function followUpPerson(item = {}) {
  const recipients = Array.isArray(item.recipientEmails) ? item.recipientEmails.filter(Boolean) : [];
  if (recipients.length) return compactEmail(recipients[0]);

  const participant = Array.isArray(item.participants) ? item.participants.find(Boolean) : '';
  if (participant) return compactEmail(participant);

  if (item.senderEmail && item.senderEmail !== item.mailboxEmail) return compactEmail(item.senderEmail);

  return '';
}

function followUpTooltip(item = {}) {
  const recipients = Array.isArray(item.recipientEmails) ? item.recipientEmails.filter(Boolean) : [];

  return [
    recipients.length ? `Follow-up recipient(s): ${recipients.join(', ')}` : '',
    item.mailboxEmail ? `Your inbox: ${item.mailboxEmail}` : '',
  ].filter(Boolean).join('\n');
}

function canCreateManualDraft(item = {}) {
  const state = String(item.automationState || '').toLowerCase();
  if (['mailbox_disconnected', 'mailbox_reconnect_required'].includes(state)) return false;
  return ['watching', 'draft_deleted', 'failed', 'waiting_after_followup'].includes(item.status || 'watching');
}

function researchProviders(item = {}) {
  const providers = item?.lastResearch?.providers || [];
  return Array.isArray(providers) ? providers : [];
}

function researchSummaryMeta(item = {}) {
  const research = item?.lastResearch || {};
  if (!research?.runId) return null;

  const providers = researchProviders(item);
  const threadUseful = Boolean(research.threadBrief?.usable);
  const externalUseful = Boolean(research.brief?.usable && providers.length);

  if (threadUseful && providers.length) {
    return { label: 'Thread + external', bg: 'rgba(70,201,142,.11)', color: '#176b49' };
  }

  if (threadUseful) {
    return { label: 'Thread researched', bg: 'rgba(141,149,255,.10)', color: '#454cba' };
  }

  if (externalUseful) {
    return { label: 'External research', bg: 'rgba(70,201,142,.11)', color: '#176b49' };
  }

  return { label: 'Research complete', bg: 'rgba(26,29,49,.06)', color: '#575b74' };
}

function nextActionMeta(item = {}, { autoCreateDrafts = false } = {}) {
  const state = String(item.automationState || '').toLowerCase();
  const actionAt = item.nextActionAt || item.nextFollowUpAt;
  const when = formatDate(actionAt, { includeTime: true });
  const actionMs = actionAt ? Date.parse(actionAt) : NaN;
  const isDueNow = Number.isFinite(actionMs) && actionMs <= Date.now();
  const error = String(item.lastError || '').trim();

  if (state === 'mailbox_disconnected') {
    return {
      label: 'Mailbox disconnected',
      detail: '',
      tone: 'warning',
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />,
    };
  }

  if (state === 'mailbox_reconnect_required') {
    return {
      label: 'Reconnect mailbox',
      detail: '',
      tone: 'warning',
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />,
    };
  }

  if (item.status === 'needs_review' || state === 'needs_review') {
    const review = item.crossThreadReview || {};
    return {
      label: 'Review newer email',
      detail: review.reason || 'This contact emailed you in another thread and it may change this follow-up.',
      tone: 'warning',
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 17 }} />,
    };
  }

  if (state === 'send_failed_draft_saved') {
    return {
      label: 'Send failed · draft saved',
      detail: error || 'Open the draft in your mailbox and review it.',
      tone: 'error',
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 17 }} />,
    };
  }

  if (state === 'failed' || item.status === 'failed') {
    return {
      label: 'Needs attention',
      detail: error || 'Nudge could not complete the next step.',
      tone: 'error',
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 17 }} />,
    };
  }

  if (state === 'awaiting_approval') {
    return {
      label: 'Waiting for approval',
      detail: when ? `Scheduled after approval · ${when}` : 'Approve this conversation before Nudge sends.',
      tone: 'warning',
      icon: <LockRoundedIcon sx={{ fontSize: 16 }} />,
    };
  }

  if (state === 'not_on_allowlist') {
    return {
      label: 'Not approved to send',
      detail: 'This recipient is outside your auto-send allow-list.',
      tone: 'warning',
      icon: <LockRoundedIcon sx={{ fontSize: 16 }} />,
    };
  }

  if (state === 'out_of_credits') {
    return {
      label: 'Paused · out of credits',
      detail: 'Draft creation resumes when credits are available.',
      tone: 'warning',
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 17 }} />,
    };
  }

  if (state === 'manual_only' || !autoCreateDrafts) {
    return {
      label: 'Suggested follow-up',
      date: when,
      detail: '',
      tone: 'neutral',
      icon: null,
      quiet: true,
    };
  }

  if (item.status === 'drafted' || state === 'draft_ready') {
    return {
      label: 'Draft ready',
      detail: '',
      tone: 'warning',
      icon: <DraftsOutlinedIcon sx={{ fontSize: 16 }} />,
    };
  }

  if (item.status === 'waiting_after_followup' || state === 'waiting_for_reply') {
    return {
      label: 'Waiting for reply',
      date: when ? `Next follow-up ${when}` : '',
      detail: '',
      tone: 'success',
      icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />,
    };
  }

  if (item.status === 'replied') {
    return {
      label: 'Reply received',
      detail: '',
      tone: 'success',
      icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />,
    };
  }

  if (item.mode === 'send' || state === 'scheduled_send') {
    return {
      label: isDueNow ? 'Send due now' : 'Send scheduled',
      date: isDueNow ? '' : when,
      detail: (item.autoSendPolicy === 'approval' && !item.autoSendApproved)
        ? 'Approval required'
        : '',
      tone: 'neutral',
      icon: <SendRoundedIcon sx={{ fontSize: 16 }} />,
    };
  }

  return {
    label: isDueNow ? 'Draft due now' : 'Draft scheduled',
    date: isDueNow ? '' : when,
    detail: '',
    tone: 'neutral',
    icon: null,
    quiet: true,
  };
}

function NextAction({ item, autoCreateDrafts = false }) {
  const meta = nextActionMeta(item, { autoCreateDrafts });

  const styles = {
    error: { bg: 'rgba(255,142,122,.09)', color: '#9f3b2c' },
    warning: { bg: 'rgba(255,179,71,.10)', color: '#8a5400' },
    success: { bg: 'rgba(70,201,142,.09)', color: '#176b49' },
    neutral: { bg: 'rgba(26,29,49,.04)', color: '#45495d' },
  }[meta.tone] || { bg: 'rgba(26,29,49,.04)', color: '#45495d' };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={meta.icon ? .65 : 0} alignItems="center">
        {meta.icon && (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1.1,
              display: 'grid',
              placeItems: 'center',
              bgcolor: styles.bg,
              color: styles.color,
              flex: '0 0 auto',
            }}
          >
            {meta.icon}
          </Box>
        )}

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: meta.quiet ? 'text.primary' : styles.color,
              fontWeight: meta.quiet ? 400 : 700,
              lineHeight: 1.25,
            }}
          >
            {meta.label}
          </Typography>

          {meta.date && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: .15, fontWeight: 400, lineHeight: 1.25 }}
            >
              {meta.date}
            </Typography>
          )}
        </Box>
      </Stack>

      {meta.detail && (
        <Typography
          variant="caption"
          color={meta.tone === 'error' ? 'error.main' : 'text.secondary'}
          sx={{ display: 'block', mt: .3, lineHeight: 1.3 }}
        >
          {meta.detail}
        </Typography>
      )}
    </Box>
  );
}

export function OpportunityReviewTable({
  items = [],
  loading = false,
  error = '',
  actionId = '',
  researchActionId = '',
  onRefresh,
  onAddConversation,
  onDraft,
  onBulkDraft,
  onResearchDraft,
  onBulkResearchDraft,
  onViewResearch,
  onFeedback,
  onAutoSendApproval,
  onOpenSafety,
  researchEnabled = false,
  onConnectResearch,
  creditsExhausted = false,
  autoCreateDrafts = false,
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const bulkBusy = actionId === '__bulk__' || researchActionId === '__bulk__';

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (Date.parse(dateValue(b)) || 0) - (Date.parse(dateValue(a)) || 0)),
    [items],
  );

  const draftableItems = useMemo(
    () => sortedItems.filter((item) => canCreateManualDraft(item) && itemKey(item)),
    [sortedItems],
  );

  const selectedDraftableItems = useMemo(
    () => draftableItems.filter((item) => selectedIds.has(itemKey(item))),
    [draftableItems, selectedIds],
  );

  const allDraftableSelected = draftableItems.length > 0 && draftableItems.every((item) => selectedIds.has(itemKey(item)));
  const someDraftableSelected = draftableItems.some((item) => selectedIds.has(itemKey(item)));

  useEffect(() => {
    const valid = new Set(items.map(itemKey).filter(Boolean));

    setSelectedIds((current) => {
      const next = new Set([...current].filter((id) => valid.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [items]);

  const toggleOne = (item) => {
    const id = itemKey(item);
    if (!id || !canCreateManualDraft(item)) return;

    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllDraftable = () => {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (allDraftableSelected) {
        draftableItems.forEach((item) => next.delete(itemKey(item)));
      } else {
        draftableItems.forEach((item) => next.add(itemKey(item)));
      }

      return next;
    });
  };

  const createSelectedDrafts = async () => {
    if (!selectedDraftableItems.length || bulkBusy || creditsExhausted) return;

    if (onBulkDraft) await onBulkDraft(selectedDraftableItems);
    else {
      for (const item of selectedDraftableItems) {
        await onDraft?.(item);
      }
    }

    setSelectedIds(new Set());
  };

  const researchSelected = async () => {
    if (!researchEnabled) {
      onConnectResearch?.();
      return;
    }

    if (!selectedDraftableItems.length || bulkBusy || creditsExhausted) return;

    if (onBulkResearchDraft) await onBulkResearchDraft(selectedDraftableItems);
    else {
      for (const item of selectedDraftableItems) {
        await onResearchDraft?.(item);
      }
    }

    setSelectedIds(new Set());
  };

  return (
    <Card sx={{ mt: 3, overflow: 'hidden', borderRadius: 2, boxShadow: '0 10px 30px rgba(26,29,49,.05)' }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1.5} alignItems={{ sm: 'center' }}>
          <Box>
            <Typography variant="h3" sx={{ fontSize: 23 }}>Missed opportunities</Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: .4 }}>
              Review conversations that may need a follow-up.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
            {selectedDraftableItems.length > 0 && (
              <>
                <Button
                  variant="contained"
                  startIcon={actionId === '__bulk__' ? <CircularProgress color="inherit" size={16} /> : <DraftsOutlinedIcon />}
                  onClick={createSelectedDrafts}
                  disabled={loading || bulkBusy || creditsExhausted}
                  sx={{ px: 1.25, py: .55, minHeight: 34 }}
                >
                  {actionId === '__bulk__' ? 'Creating…' : `Draft ${selectedDraftableItems.length}`}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={researchActionId === '__bulk__' ? <CircularProgress size={16} /> : undefined}
                  onClick={researchSelected}
                  disabled={loading || bulkBusy || creditsExhausted}
                  sx={{ whiteSpace: 'nowrap', px: 1.25, py: .55, minHeight: 34 }}
                >
                  {researchActionId === '__bulk__' ? 'Researching…' : `Research & Draft (${selectedDraftableItems.length})`}
                </Button>
              </>
            )}

            {onAddConversation && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={onAddConversation}
                disabled={loading || bulkBusy}
                sx={{ px: 1.15, py: .5, minHeight: 32, whiteSpace: 'nowrap' }}
              >
                Add conversation
              </Button>
            )}

            <Button
              variant="text"
              startIcon={loading ? <CircularProgress size={16} /> : <SyncRoundedIcon />}
              onClick={onRefresh}
              disabled={loading || bulkBusy}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {!researchEnabled && (
          <Alert severity="info" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={onConnectResearch}>View upgrade</Button>}>
            Research & Draft is not included in your current plan.
          </Alert>
        )}

        <Box
          sx={{
            mt: 2,
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: '34px minmax(250px,1.2fr) 125px minmax(180px,.9fr) 90px 275px',
            gap: 1.2,
            px: 1.2,
            pb: .8,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Checkbox
            size="small"
            checked={allDraftableSelected}
            indeterminate={!allDraftableSelected && someDraftableSelected}
            disabled={!draftableItems.length || loading || bulkBusy}
            onChange={toggleAllDraftable}
            sx={{ p: .25, alignSelf: 'center' }}
            inputProps={{ 'aria-label': 'Select all draftable opportunities' }}
          />
          <Typography variant="caption" color="text.secondary" fontWeight={800}>Conversation</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={800}>Status</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={800}>Next action</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={800}>Last touch</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textAlign: 'right' }}>Actions</Typography>
        </Box>

        {loading && !sortedItems.length ? (
          <Stack py={4} alignItems="center"><CircularProgress size={28} /></Stack>
        ) : sortedItems.length ? (
          <Stack sx={{ mt: { xs: 1, md: 0 } }}>
            {sortedItems.map((item) => {
              const id = itemKey(item);
              const draftBusy = actionId === item.threadId || actionId === item.id;
              const researchBusy = researchActionId === item.threadId || researchActionId === item.id;
              const busy = bulkBusy || draftBusy || researchBusy;
              const person = followUpPerson(item);
              const category = categoryLabel(item);
              const draftable = canCreateManualDraft(item);
              const researchMeta = researchSummaryMeta(item);
              const autoSendSequenceState = ['watching', 'waiting_after_followup', 'failed'].includes(item.status || 'watching');
              const awaitingApproval = item.automationState === 'awaiting_approval'
                || (autoSendSequenceState && item.mode === 'send' && item.autoSendPolicy === 'approval' && !item.autoSendApproved);
              const approved = autoSendSequenceState && item.mode === 'send' && item.autoSendPolicy === 'approval' && item.autoSendApproved;
              const blockedByAllowlist = item.automationState === 'not_on_allowlist';

              return (
                <Box
                  key={id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '32px 1fr',
                      md: '34px minmax(250px,1.2fr) 125px minmax(180px,.9fr) 90px 275px',
                    },
                    gap: { xs: 1.1, md: 1.2 },
                    alignItems: 'center',
                    px: { xs: .4, md: 1.2 },
                    py: 1.45,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-of-type': { borderBottom: 0 },
                    '&:hover': { bgcolor: 'rgba(26,29,49,.018)' },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={Boolean(id && selectedIds.has(id))}
                    disabled={busy || !draftable}
                    onChange={() => toggleOne(item)}
                    sx={{ p: .25, alignSelf: { xs: 'flex-start', md: 'center' }, mt: { xs: .35, md: 0 } }}
                    inputProps={{ 'aria-label': `Select ${cleanSubject(item.subject)}` }}
                  />

                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={.8} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography noWrap sx={{ fontWeight: 700, minWidth: 0, maxWidth: { xs: '100%', md: 390 } }}>
                        {cleanSubject(item.subject)}
                      </Typography>

                      {category && (
                        <Chip
                          size="small"
                          label={category}
                          variant="outlined"
                          sx={{ height: 22, borderRadius: 1, borderColor: 'rgba(26,29,49,.16)', '& .MuiChip-label': { px: .8, fontSize: 11.2 } }}
                        />
                      )}

                      {researchMeta && (
                        <Tooltip title="View saved research">
                          <Chip
                            size="small"
                            clickable
                            onClick={() => onViewResearch?.(item)}
                            icon={<AutoAwesomeRoundedIcon />}
                            label={researchMeta.label}
                            sx={{
                              height: 22,
                              borderRadius: 1,
                              bgcolor: researchMeta.bg,
                              color: researchMeta.color,
                              cursor: 'pointer',
                              '& .MuiChip-label': { px: .65, fontSize: 11.2, fontWeight: 800 },
                              '& .MuiChip-icon': { fontSize: 14, color: 'inherit' },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>

                    {person && (
                      <Tooltip title={followUpTooltip(item)} arrow>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: .35, maxWidth: 520, fontSize: 11.5 }}>
                          {person}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>

                  <Box sx={{ gridColumn: { xs: '2 / -1', md: 'auto' } }}>
                    <StatusChip status={item.status} />
                  </Box>

                  <Box sx={{ gridColumn: { xs: '2 / -1', md: 'auto' } }}>
                    <NextAction item={item} autoCreateDrafts={autoCreateDrafts} />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: { xs: '2 / -1', md: 'auto' } }}>
                    {formatDate(dateValue(item)) || '—'}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={.6}
                    alignItems="center"
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ gridColumn: { xs: '2 / -1', md: 'auto' } }}
                  >
                    {item.status === 'needs_review' && (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busy}
                          onClick={() => onFeedback?.(item, 'keep_following')}
                        >
                          Keep following
                        </Button>
                        <Button
                          size="small"
                          color="inherit"
                          variant="text"
                          disabled={busy}
                          onClick={() => onFeedback?.(item, 'dismiss')}
                        >
                          Dismiss
                        </Button>
                      </>
                    )}

                    {item.status !== 'needs_review' && awaitingApproval && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SendRoundedIcon />}
                        disabled={busy}
                        onClick={() => onAutoSendApproval?.(item, true)}
                      >
                        {draftBusy ? '…' : 'Approve send'}
                      </Button>
                    )}

                    {approved && (
                      <Button
                        size="small"
                        color="inherit"
                        variant="text"
                        disabled={busy}
                        onClick={() => onAutoSendApproval?.(item, false)}
                      >
                        Revoke
                      </Button>
                    )}

                    {blockedByAllowlist && (
                      <Button size="small" variant="outlined" onClick={onOpenSafety} disabled={busy}>
                        Safety settings
                      </Button>
                    )}

                    {item.status !== 'needs_review' && draftable && (
                      <Button
                        size="small"
                        variant={awaitingApproval || blockedByAllowlist ? 'outlined' : 'contained'}
                        startIcon={<DraftsOutlinedIcon sx={{ fontSize: 15 }} />}
                        disabled={busy || creditsExhausted}
                        onClick={() => onDraft?.(item)}
                        sx={{
                          px: .9,
                          py: .35,
                          minWidth: 'auto',
                          minHeight: 30,
                          borderRadius: 1.2,
                          fontSize: 12.5,
                          whiteSpace: 'nowrap',
                          '& .MuiButton-startIcon': { mr: .45 },
                        }}
                      >
                        {draftBusy ? '…' : 'Draft'}
                      </Button>
                    )}

                    {item.status !== 'needs_review' && draftable && (
                      <Tooltip title={researchEnabled ? 'Analyze the existing thread first, then optionally use connected external research.' : 'Research & Draft is not included in this plan'}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={researchBusy ? <CircularProgress size={13} /> : undefined}
                            disabled={busy || creditsExhausted}
                            sx={{
                              px: .9,
                              py: .35,
                              minWidth: 'auto',
                              minHeight: 30,
                              borderRadius: 1.2,
                              fontSize: 12.5,
                              whiteSpace: 'nowrap',
                              '& .MuiButton-startIcon': { mr: .45 },
                            }}
                            onClick={() => {
                              if (!researchEnabled) onConnectResearch?.();
                              else onResearchDraft?.(item);
                            }}
                          >
                            {researchBusy ? '…' : 'Research & Draft'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}

                    {item.status !== 'needs_review' && <Tooltip title="Good opportunity">
                      <span>
                        <IconButton size="small" disabled={busy} onClick={() => onFeedback?.(item, 'good')}>
                          <ThumbUpAltOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>}

                    {item.status !== 'needs_review' && <Tooltip title="Dismiss">
                      <span>
                        <IconButton size="small" disabled={busy} onClick={() => onFeedback?.(item, 'dismiss')}>
                          <ThumbDownAltOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            No opportunities loaded yet. Click Find conversations to scan your sent mail.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
